import {
  ITEM_KIND,
  countItems,
  countWhere,
  filterGroups,
} from "../../StrategyCanvas/StrategyCanvasSummary";

// Builds the "Review Summary" panel's group tree — the Annual OKR equivalent of
// `ExecutionBlueprintReviewSummary.js`, itself the equivalent of Canvas's.
//
// `ITEM_KIND` / `countItems` / `countWhere` / `filterGroups` are imported
// rather than reimplemented, for the reason EXW's file gives: `SummaryPanel`'s
// `SummaryItem` compares against `ITEM_KIND`'s exact string values, so a local
// copy with different values would silently stop matching.
//
// ⚠️ ONE GROUP, not one per section. EXW has four WIG sections that are already
// the top-level grouping; Annual OKR's §6.3 returns a FLAT list of Objectives
// with no grouping of any kind — `wig_label` is a provenance label on an
// Objective, not a container, and it is null on freestanding ones. Inventing
// groups from it would put half the board under "Uncategorised". So the
// Objectives are one group and the overall assessment is a second.
export { ITEM_KIND, countItems, countWhere, filterGroups };

const hasText = (comment) =>
  !!(comment?.comment_text && comment.comment_text.trim());

// ⚠️ THESE ARE CLASS SUFFIXES, NOT HEX VALUES. `SummaryPanel` renders the
// group's colour rail as `sp-section-rail--${group.color}`, and its stylesheet
// defines exactly four: `teal`, `dark`, `core`, `dna`. This file passed
// "#056C75" and "#7A6FF0", which produced `sp-section-rail--#056C75` — a class
// that matches nothing — so every group in the Review Summary rendered with an
// invisible rail. That is the missing colour band reported 2026-09-24.
//
// `core` IS #056c75, so the Objectives group keeps exactly the colour this file
// was asking for. Overall assessment takes `dna` (#08b3c3): the purple it wanted
// does not exist in the shared scale, and adding a fifth colour to a stylesheet
// three features share to give one group its own tint is not worth it.
const OBJECTIVE_COLOR = "core";
const OVERALL_COLOR = "dna";

export const buildReviewSummary = (review) => {
  if (!review) return [];
  const groups = [];

  const items = [];
  (review.objectives || []).forEach((objective) => {
    const comments = (objective.comments || []).filter(hasText);
    const flagged = objective.review_status === "to_discuss";
    // Only surfaced if there is something to say about it — an accepted
    // Objective with no comment doesn't need a line in the summary. Same rule
    // EXW applies to its items.
    if (!flagged && !comments.length) return;
    items.push({
      id: `objective-${objective.annual_objective_id}`,
      kind: ITEM_KIND.BLOCK,
      title: objective.objective_statement || "Untitled Objective",
      status: objective.review_status,
      comments,
    });
  });

  if (items.length) {
    groups.push({
      key: "objectives",
      // ⚠️ NO BAND. `SummaryPanel` renders `band` ABOVE `name`, so
      // "COMPANY OKRS" + "Objectives" stacked two labels over every single
      // group. The band is the one that goes: this whole feature IS the Company
      // OKRs — there are no Leader or Team OKRs (§10 scope) — so a band naming
      // the only kind of OKR that exists groups one thing under its own name,
      // and the screen header two panels left already says "Company OKRs —
      // <company>".
      //
      // `name` STAYS, unlike the live review agenda's equivalent group, and the
      // difference is deliberate rather than drift: this panel has a second
      // group ("Overall assessment") to tell it apart from, and `SummaryPanel`
      // draws the item count in the same row as the heading — so an empty
      // `name` here would leave that count chip floating on a blank line.
      band: null,
      name: "Company OKRs",
      color: OBJECTIVE_COLOR,
      items,
    });
  }

  const overall = (review.overall_comments || []).filter(hasText);
  if (overall.length) {
    groups.push({
      key: "overall",
      // The band goes for the same reason; the NAME stays, because this one is
      // a distinction worth drawing — a comment on the board as a whole rather
      // than on a row. Only ever rendered when there is an overall comment, so
      // it never appears as a lone heading over nothing.
      band: null,
      name: "Overall assessment",
      color: OVERALL_COLOR,
      items: overall.map((comment) => ({
        id: `overall-${comment.coach_comment_id}`,
        kind: ITEM_KIND.SECTION,
        title: "Overall assessment",
        status: null,
        comments: [comment],
      })),
    });
  }

  return groups;
};
